import React, {useEffect, useState, useContext, useRef, useCallback} from "react";
import {DatePicker, Form, Input, Select} from 'antd';
import axios from "@common/axios";
import {UserContext} from "@context/UserContext";
import {debounce} from 'lodash';
import Button from "@common/Button";
import GenericDialog from "@common/GenericDialog";
import CircularLoader from "@common/CircularLoader";
import EditorTinyMCE from "@common/EditorTinyMCE";

const ComposeMessageDialog = ({
                                  isOpen,
                                  onClose,
                                  onMessageSent,
                                  selectedClassId = null
                              }) => {
    const {user} = useContext(UserContext);
    const [isComposeLoading, setComposeLoading] = useState(true);
    const [recipientTypeVal, setRecipientTypeVal] = useState(null);
    const [initRecipientTypeVal, setInitRecipientTypeVal] = useState(null);
    const [recipientTypes, setRecipientTypes] = useState([]);
    const [schoolClasses, setSchoolClasses] = useState([]);
    const [suggestedUsers, setSuggestedUsers] = useState([]);
    const [suggestedUsersTop, setSuggestedUsersTop] = useState(null);
    const [suggestedUsersLeft, setSuggestedUsersLeft] = useState(null);
    const [suggestedUsersWidth, setSuggestedUsersWidth] = useState(null);
    const [isGettingNameSuggestions, setGettingNameSuggestions] = useState(false);
    const [toValue, setToValue] = useState('');
    const [extraToInstructions, setExtraToInstructions] = useState(null);
    const [errorMessage, setErrorMessage] = useState(null);
    const [composeMessageContent, setComposeMessageContent] = useState(null);
    const [isEditorFocused, setEditorFocused] = useState(false);
    const [composeForm] = Form.useForm();
    const editorRef = useRef(null);

    const FORM_MESSAGES = {
        TOO_SHORT_MESSAGE: 'Two or more characters are required.',
        NO_MATCHES_MESSAGE: 'No matches found.',
        INVALID_MATCH_MESSAGE: 'The following recipients are invalid: ',
        EXTRA_INSTRUCTIONS_CLASS: 'Select a class from the dropdown',
        EXTRA_INSTRUCTIONS_USERS: 'Please type part or all of the name',
    };

    useEffect(() => {
        if (user.subscriberType !== 'STUDENT') {
            axios.get('/web/ui/message/recipientTypes')
                .then(response => {
                    setRecipientTypes(response.data);
                });

            axios.get('/web/ui/sc/schoolClasses')
                .then(response => {
                    setSchoolClasses(response.data);
                });
        }
    }, []);

    useEffect(() => {
        if (recipientTypes.length === 0) return;
        let defaultRecipient;
        if (selectedClassId) {
            defaultRecipient = recipientTypes.find(rt => rt.value === 'STUDENTS_IN_CLASS');
        }
        if (!defaultRecipient) {
            defaultRecipient = recipientTypes[0];
        }
        setRecipientTypeVal(defaultRecipient.value);
        setInitRecipientTypeVal(defaultRecipient.value);
        composeForm.setFieldsValue({ recipientType: defaultRecipient.value });
    }, [recipientTypes, selectedClassId]);

    useEffect(() => {
        if (
            selectedClassId &&
            recipientTypeVal === 'STUDENTS_IN_CLASS' &&
            schoolClasses.length > 0 &&
            isOpen
        ) {
            const selectedClass = schoolClasses.find(cls => cls.classId === parseInt(selectedClassId));
            if (selectedClass) {
                composeForm.setFieldsValue({ to: selectedClass.className });
            }
        }
    }, [selectedClassId, recipientTypeVal, schoolClasses, isOpen]);

    useEffect(() => {
        if (!recipientTypeVal) return;

        let instructions = '';

        if (recipientTypeVal === 'STUDENTS_IN_CLASS') {
            instructions = selectedClassId ? '' : FORM_MESSAGES.EXTRA_INSTRUCTIONS_CLASS;
        } else {
            instructions = FORM_MESSAGES.EXTRA_INSTRUCTIONS_USERS;
        }

        setExtraToInstructions(instructions);
    }, [recipientTypeVal, selectedClassId]);

    // Update form message content when editor content changes
    useEffect(() => {
        if (isOpen) {
            const fieldValues = composeForm.getFieldsValue();
            if (Object.keys(fieldValues).length > 0) {
                composeForm.setFieldsValue({message: composeMessageContent});
                composeForm.validateFields(['message']);
            }
        }
    }, [composeMessageContent, isOpen]);

    // Handle suggested users display and event listeners
    useEffect(() => {
        if (suggestedUsers.length > 0) {
            setSuggestedUserVals();
            setGettingNameSuggestions(false);

            document.addEventListener('click', resetSuggestedUsers);
            document.addEventListener('focusin', resetSuggestedUsers);
            window.addEventListener('resize', resetSuggestedUsers);

            return () => {
                document.removeEventListener('click', resetSuggestedUsers);
                document.removeEventListener('focusin', resetSuggestedUsers);
                window.removeEventListener('resize', resetSuggestedUsers);
            };
        }
    }, [suggestedUsers]);

    // Handle error message validation
    useEffect(() => {
        if (errorMessage) {
            setGettingNameSuggestions(false);
        }
        if (isOpen) {
            composeForm.validateFields(['to']);
        }
    }, [errorMessage, isOpen]);

    const onHandleEditorFocus = () => {
        setEditorFocused(true);
        setSuggestedUsers([]);
    };

    const onHandleEditorBlur = () => {
        setEditorFocused(false);
    };

    const handleCloseDialog = () => {
        setToValue('');
        setSuggestedUsers([]);
        setRecipientTypeVal(initRecipientTypeVal);
        setErrorMessage(null);
        composeForm.resetFields();
        onClose();
    };

    const handleOnRecipientTypeChange = (recipientValue) => {
        setToValue('');
        composeForm.resetFields(['to']);
        setRecipientTypeVal(recipientValue);
        setErrorMessage(null);
    };

    const onToFieldChange = (event) => {
        if (!suggestedUsersLeft) {
            setSuggestedUserVals();
        }
        setErrorMessage(null);
        setSuggestedUsers([]);
        setToValue(event.target.value);

        const latestToValue = event.target.value.split(/,\s*/).pop();
        if (latestToValue.trim() === '') {
            return;
        }

        setGettingNameSuggestions(true);
        handleToFieldChange(latestToValue.trim());
    };

    const handleToFieldChange = useCallback(
        debounce((term) => {
            if (term.length >= 2) {
                axios.get('/message/getNameCompletions.action', {params: {term: term, recipientType: composeForm.getFieldValue('recipientType')}})
                    .then(response => {
                        if (response.data.length === 0) {
                            setErrorMessage(FORM_MESSAGES.NO_MATCHES_MESSAGE);
                            setSuggestedUsers([]);
                        } else {
                            setErrorMessage(null);
                            setSuggestedUsers(response.data);
                        }
                    }).finally(() => {
                    setGettingNameSuggestions(false);
                });
            }
            if (term.length < 2) {
                setErrorMessage(FORM_MESSAGES.TOO_SHORT_MESSAGE);
            }
        }, 500), []);

    const validateToField = (_, value) => {
        if (errorMessage) {
            return Promise.reject(new Error(errorMessage.toString()));
        }
        if (!value) {
            return Promise.reject(new Error('Recipients are required.'));
        }
        return Promise.resolve();
    };

    const handleAddSuggestedUser = (suggestedUserStr) => {
        const splitUsers = splitValue(toValue);
        splitUsers.pop();
        const newValue = [...splitUsers, suggestedUserStr, ''].join(', ');
        setToValue(newValue);
        composeForm.setFieldsValue({to: newValue});
        setSuggestedUsers([]);
        focusToElement();
    };

    const focusToElement = () => {
        const toElInput = document.querySelector('#to textarea');
        if (toElInput) toElInput.focus();
    };

    const setSuggestedUserVals = () => {
        const toEl = document.getElementById('to');
        if (toEl) {
            const toElRect = toEl.getBoundingClientRect();
            setSuggestedUsersTop(toElRect.bottom);
            setSuggestedUsersLeft(toElRect.left);
            setSuggestedUsersWidth(toElRect.width);
        }
    };

    const resetSuggestedUsers = (e) => {
        const suggestionEl = document.getElementById('suggested-options');

        if ((e.type == 'click' && e.target.parentElement != undefined && e.target.parentElement.id !== 'to')
            || (e.type == 'focusin' && !suggestionEl.contains(e.target))
            || e.type == 'resize') {
            setSuggestedUsers([]);
            if (e.type == 'focusin') focusToElement();
        }
    };

    const doSendMessage = async () => {
        const fieldValues = {...composeForm.getFieldsValue()};

        if (fieldValues.subject === undefined || fieldValues.message === undefined || fieldValues.to === undefined) {
            composeForm.validateFields();
            return;
        }

        if (isNaN(parseInt(fieldValues.to))) {
            const splitUsers = splitValue(fieldValues.to);
            if (splitUsers[splitUsers.length - 1].trim() === '') {
                splitUsers.pop();
                fieldValues.to = splitUsers.join(', ');
            }
        }

        return axios.get('/message/validateRecipients.action', {params: {to: fieldValues.to, recipientType: fieldValues.recipientType}})
            .then(result => {
                if (result.data.length > 0) {
                    setErrorMessage(FORM_MESSAGES.INVALID_MATCH_MESSAGE + result.data.join(', '));
                } else {
                    if (fieldValues.expiry) fieldValues.expiry = fieldValues.expiry.format(user?.getDateFormatString?.() ?? 'DD/MM/YYYY');
                    return axios.post('/message/sendMessage.action', null, {params: fieldValues})
                        .then(response => {
                            if (response.data.success) {
                                setErrorMessage(null);
                                displayToastMessage('Message', 'Message sent successfully', 'success', null, 10000);
                                onMessageSent && onMessageSent();
                                handleCloseDialog();
                            } else if (response.data.message) {
                                setErrorMessage(response.data.message);
                            } else {
                                setErrorMessage('Error sending message');
                            }
                        }).catch(error => {
                            console.error('error', error);
                            setErrorMessage('Error sending message');
                        });
                }
            });
    };

    const splitValue = (value) => {
        return value.split(/,\s*/);
    };

    return (
        <>
            <GenericDialog
                title={'Compose message'}
                open={isOpen}
                onClose={handleCloseDialog}
                buttons={[
                    {
                        style: {backgroundColor: '#ffffff'},
                        name: 'Cancel',
                        extraProps: {type: 'text'}
                    },
                    {
                        name: 'Send',
                        callback: doSendMessage
                    }
                ]}
            >
                <CircularLoader visible={isComposeLoading} pageCentered={true} displayInElement={true} spacing={true} />
                <Form className={'ch-form'} form={composeForm} initialValues={{recipientType: initRecipientTypeVal}} hidden={isComposeLoading}>
                    <Form.Item label={'Subject'} name={'subject'} rules={[{required: true, message: 'Subject is required.'}]}>
                        <Input />
                    </Form.Item>
                    <Form.Item label="Message" name="message" rules={[{required: true, message: 'Message is required.'}]}>
                        <EditorTinyMCE
                            ref={editorRef}
                            isFocused={isEditorFocused}
                            width={false}
                            height={150}
                            showSource={true}
                            setContent={setComposeMessageContent}
                            setLoading={setComposeLoading}
                            handleOnFocus={onHandleEditorFocus}
                            handleOnBlur={onHandleEditorBlur}
                        />
                    </Form.Item>
                    <Form.Item label="Expiry" name="expiry">
                        <DatePicker format={user?.getDateFormatString?.() ?? 'DD/MM/YYYY'} />
                    </Form.Item>
                    <Form.Item label={'Recipients'} name={'recipientType'}>
                        <Select
                            virtual={false}
                            autoClearSearchValue={true}
                            onChange={handleOnRecipientTypeChange}
                            disabled={!!selectedClassId}
                        >
                            {recipientTypes.length > 0 && recipientTypes.map((recipientType) => {
                                return <Select.Option key={recipientType.value} value={recipientType.value}>{recipientType.display}</Select.Option>;
                            })}
                        </Select>
                    </Form.Item>
                    <Form.Item label={'To'} name={'to'} rules={[{validator: validateToField}]}
                               extra={extraToInstructions}>
                        {recipientTypeVal && recipientTypeVal === 'STUDENTS_IN_CLASS' &&
                            <Select
                                virtual={false}
                                autoClearSearchValue={true}
                                disabled={!!selectedClassId}
                            >
                                {schoolClasses.length > 0 && schoolClasses.map((schoolClass) => {
                                    return <Select.Option key={schoolClass.classId} value={schoolClass.className}>{schoolClass.className}</Select.Option>;
                                })}
                            </Select>
                        }
                        {recipientTypeVal && recipientTypeVal !== 'STUDENTS_IN_CLASS' &&
                            <div className={'flexSpaceBetween flexRow'}>
                                <div style={{marginLeft: '-50px', marginTop: '30px'}}>
                                    <CircularLoader visible={isGettingNameSuggestions} coreOnly={true}/>
                                </div>
                                <Input.TextArea rows={3} value={toValue} onChange={onToFieldChange}/>
                            </div>
                        }
                    </Form.Item>
                </Form>
                {suggestedUsers.length > 0 &&
                    <div className={'ch-suggester-options-wrapper'} tabIndex={-1}
                         style={{top: suggestedUsersTop + 'px', left: suggestedUsersLeft + 'px', width: suggestedUsersWidth + 'px'}}>
                        <div id={'suggested-options'} className={'ch-suggester-options padding--m'} tabIndex={-1}>
                            {suggestedUsers.map((suggestedUserVal) => {
                                return <Button type={'text'} key={suggestedUserVal} aria-label={'Add user ' + suggestedUserVal} onClick={() => handleAddSuggestedUser(suggestedUserVal)}>{suggestedUserVal}</Button>;
                            })}
                        </div>
                    </div>
                }
            </GenericDialog>
        </>
    );
};

export default ComposeMessageDialog;